import {
    GridCellValue,
    GridRowsProp,
    GridColDef,
    GridComponentProps,
    GridRowData,
    GridRenderCellParams,
    GridColumnHeaderParams,
    DataGrid,
} from '@mui/x-data-grid';
import * as React from 'react';

export type RowDataBaseType = {
    id: string;
    [key: string]: unknown;
};

export type TypedDataGridCellValue = GridCellValue;

export type TypedDataGridRowData<RowData extends RowDataBaseType> = GridRowData & RowData;

export type TypedDataGridRowsProp<RowData extends RowDataBaseType> = GridRowsProp &
    Readonly<Array<TypedDataGridRowData<RowData>>>;

export type TypedDataGridRenderCellParams<T = unknown> = Omit<GridRenderCellParams, 'value'> & {
    value: T;
};

export type TypedDataGridRenderHeaderParams = GridColumnHeaderParams;

export type TypedDataGridColDefForColumn<RowData extends RowDataBaseType, Column extends keyof RowData> = {
    field: Column;
    renderCell?: (params: TypedDataGridRenderCellParams<RowData[Column]>) => React.ReactNode;
    renderHeader?: (params: TypedDataGridRenderHeaderParams) => React.ReactNode;
};

export type TypedDataGridColDef<
    RowData extends RowDataBaseType = RowDataBaseType,
    C extends keyof RowData = keyof RowData,
> = Omit<GridColDef, 'field' | 'renderCell' | 'renderHeader'> &
    (C extends keyof RowData ? TypedDataGridColDefForColumn<RowData, C> : never);

export type TypedDataGridProps<RowData extends RowDataBaseType, FCP = unknown> = Omit<
    GridComponentProps,
    'columns' | 'signature'
> & {
    columns: Array<TypedDataGridColDef<RowData>>;
    rows: TypedDataGridRowsProp<RowData>;
    footer?: {
        component: React.FC<FCP>;
        props: FCP;
    };
} & {
    pagination?: true | undefined;
};

export function TypedDataGrid<RowData extends RowDataBaseType, FCP = unknown>({
    footer,
    ...props
}: TypedDataGridProps<RowData, FCP>): JSX.Element {
    const components = React.useMemo(
        () => ({
            Footer: footer?.component,
        }),
        [footer?.component],
    );
    const componentsProps = React.useMemo(
        () => ({
            footer: footer?.props,
        }),
        [footer?.props],
    );
    return (
        <DataGrid
            {...(props as unknown as GridComponentProps & {
                pagination?: true | undefined;
            })}
            components={components}
            componentsProps={componentsProps}
        />
    );
}
TypedDataGrid.displayName = 'TypedDataGrid';
